/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {RangeStatus} from './RangeStatusBadge';
import {TagsTabKey} from './TagsCoachingText';
import {TagAssetType, TagAssignmentStatus, TagTitleRow} from './TagTitleRow';

export interface TagTitleRowData {
  id: string;
  assetType: TagAssetType;
  assetName: string;
  range: RangeStatus;
  assignmentStatus?: TagAssignmentStatus;
  assignedToName?: string;
  lastKnownLocation?: string;
  onNameClick?: () => void;
}

export interface TagTitleListProps {
  context: TagsTabKey;
  rows: TagTitleRowData[];
}

const useStyles = makeStyles(() => ({
  list : {
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%',
    gap           : '8px'
  }
}));

/**
 * TagTitleList renders the list of `TagTitleRow`s for the Tags accordion's
 * currently active tab (ION-7463) - either the Assigned tab's assigned tags
 * or the Nearby tab's in-range, not-yet-assigned tags.
 *
 * ```typescript
 *
 *   import {TagTitleList} from
 *     '@ion/dls/src/components/assetdetails/current/TagTitleList';
 *   ...
 *
 *   <TagTitleList context='assigned'
 *     rows={[
 *       {id: '1', assetType: 'tool', assetName: 'JacksBag', range: 'in'},
 *       {id: '2', assetType: 'trailer', assetName: 'Trailer', range: 'out'}
 *     ]} />
 *
 * ```
 *
 */
export const TagTitleList = ({context, rows}: TagTitleListProps) => {
  const classes = useStyles();
  return (
    <div className={classes.list}>
      {rows.map(function(row) {
        return (
          <TagTitleRow key={row.id}
            assetType={row.assetType}
            assetName={row.assetName}
            context={context}
            range={row.range}
            assignmentStatus={row.assignmentStatus}
            assignedToName={row.assignedToName}
            lastKnownLocation={row.lastKnownLocation}
            onNameClick={row.onNameClick} />
        );
      })}
    </div>
  );
};

export default memo(TagTitleList);
